import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Tabs from 'components/Tabs';
import MonthYearNavigation from 'components/ui/MonthYearNavigation';
import useUnplannedActionCount from 'hooks/useUnplannedActionCount';
import useUser from 'hooks/useUser';
import { useGetEmployeeLeaveByUserIdQuery } from 'redux/features/employeeLeaveApi';
import { LEAVE_TYPES, mapLeaveType } from 'utils/common';
import dayjs from 'lib/dayjs';
import {
  useGetLogUpdateRequestByUserIdQuery,
  useCancelLogUpdateRequestMutation,
  useBulkCancelLogUpdateRequestMutation,
} from 'redux/features/timeLogApi';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import TableSkeleton from 'components/TableSkeleton';
import { skipToken } from '@reduxjs/toolkit/query';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import CommandPalette from '../../components/ui/CommandPalette';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import UnplannedLeavesTab from './components/UnplannedLeavesTab';

const formatContact = (contactStr) => {
  if (!contactStr) return '';
  try {
    const parsed = JSON.parse(contactStr);
    return `${parsed.name} - ${parsed.number}`;
  } catch {
    return contactStr;
  }
};

const EmployeeRequests = () => {
  const user = useUser();
  const unplannedActionCount = useUnplannedActionCount(user?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const [mainTab, setMainTab] = useState(searchParams.get('mainTab') || 'leave');
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [subTab, setSubTab] = useState(searchParams.get('subTab') || 'PENDING');

  const [cancelLogUpdate] = useCancelLogUpdateRequestMutation();
  const [bulkCancelLogUpdate] = useBulkCancelLogUpdateRequestMutation();
  const [cancelModal, setCancelModal] = useState({
    isOpen: false,
    requestId: null,
    requestIds: null,
    isLoading: false,
    title: '',
    message: '',
  });

  const handleCancelLogUpdate = (id) => {
    setCancelModal({
      isOpen: true,
      requestId: id,
      requestIds: null,
      isLoading: false,
      title: 'Cancel Update Request',
      message: 'Are you sure you want to cancel this time log update request?',
    });
  };

  const confirmCancelLogUpdate = async () => {
    setCancelModal((prev) => ({ ...prev, isLoading: true }));
    try {
      if (cancelModal.requestIds) {
        await bulkCancelLogUpdate(cancelModal.requestIds).unwrap();
      } else {
        await cancelLogUpdate(cancelModal.requestId).unwrap();
      }
      toast.success('Request(s) cancelled successfully');
      setCancelModal({
        isOpen: false,
        requestId: null,
        requestIds: null,
        isLoading: false,
        title: '',
        message: '',
      });
    } catch (error) {
      console.error('Failed to cancel request:', error);
      setCancelModal((prev) => ({ ...prev, isLoading: false, isOpen: false }));
    }
  };

  const workSchedule = {
    dayStartTime: user?.work_schedule?.day_start_time,
    dayEndTime: user?.work_schedule?.day_end_time,
    breakResumeTime: user?.work_schedule?.break_resume_time,
  };

  useEffect(() => {
    const urlTab = searchParams.get('mainTab') || 'leave';
    const urlSubTab = searchParams.get('subTab') || 'PENDING';
    setMainTab(urlTab);
    setSubTab(urlSubTab);
  }, [searchParams]);

  const { data: logUpdateRequests, isLoading: isLoadingLogUpdateRequests } =
    useGetLogUpdateRequestByUserIdQuery(
      user?.id
        ? {
            id: user?.id,
            month: selectedMonth,
            year: selectedYear,
            status: subTab,
          }
        : skipToken,
      {
        skip: !user?.id || mainTab !== 'log-update',
      },
    );

  const { data: employeeLeaves, isLoading: isLoadingEmployeeLeaves } =
    useGetEmployeeLeaveByUserIdQuery(user?.id, {
      skip: !user?.id,
    });

  const toggleRowExpansion = (leaveId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(leaveId)) {
      newExpandedRows.delete(leaveId);
    } else {
      newExpandedRows.add(leaveId);
    }
    setExpandedRows(newExpandedRows);
  };

  const requestTabs = [
    {
      label: 'Leave Requests',
      icon: <Icon name="Calendar" size={16} />,
      key: 'leave',
      badge: employeeLeaves?.length || 0,
    },
    {
      label: 'Log Updates',
      icon: <Icon name="Clock" size={16} />,
      key: 'log-update',
      badge: logUpdateRequests?.data?.length || 0,
    },
    {
      label: 'Unplanned Leaves',
      icon: <Icon name="CalendarX" size={16} />,
      key: 'unplanned',
      alertCount: unplannedActionCount,
    },
  ];

  const displayStartTime = (leave) => {
    if (leave?.start_date_time && leave?.end_date_time) {
      if (leave?.dates?.length) {
        return dayjs(leave?.start_date_time).format('D MMM YYYY');
      }
      return dayjs(leave?.start_date_time).format('D MMM YYYY hh:mm A');
    }
    return 'N/A';
  };

  const displayEndTime = (leave) => {
    if (leave?.start_date_time && leave?.end_date_time) {
      if (leave?.dates?.length) {
        return dayjs(leave?.end_date_time).format('D MMM YYYY');
      }
      return dayjs(leave?.end_date_time).format('D MMM YYYY hh:mm A');
    }
    return 'N/A';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-200',
        icon: 'Clock',
      },
      APPROVED: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        icon: 'CheckCircle',
      },
      REJECTED: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-200',
        icon: 'XCircle',
      },
      CANCELLED: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: 'XCircle',
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}
      >
        <Icon name={config.icon} size={14} className="mr-1" />
        {status}
      </span>
    );
  };

  const leaveStatusTabs = [
    {
      label: 'Pending',
      key: 'PENDING',
      badge: employeeLeaves?.filter((l) => l.status === 'PENDING')?.length,
    },
    {
      label: 'Approved',
      key: 'APPROVED',
      badge: employeeLeaves?.filter((l) => l.status === 'APPROVED')?.length,
    },
    {
      label: 'Rejected',
      key: 'REJECTED',
      badge: employeeLeaves?.filter((l) => l.status === 'REJECTED')?.length,
    },
    {
      label: 'Cancelled',
      key: 'CANCELLED',
      badge: employeeLeaves?.filter((l) => l.status === 'CANCELLED')?.length,
    },
  ];

  const logUpdateStatusTabs = [
    { label: 'Pending', key: 'PENDING' },
    { label: 'Approved', key: 'APPROVED' },
    { label: 'Rejected', key: 'REJECTED' },
    { label: 'Cancelled', key: 'CANCELLED' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen ">
      <Header />
      <Sidebar />
      <CommandPalette />

      <main className="lg:ml-60 pt-16">
        <motion.div
          className="px-4 sm:px-6 lg:px-8 py-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Page Header */}
          <motion.div className="mb-6" variants={itemVariants}>
            <h1 className="text-2xl font-bold text-gray-900">My Requests</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage and track your leave and checkout requests
            </p>
          </motion.div>

          {/* Main Tabs */}
          <motion.div className="mb-6" variants={itemVariants}>
            <Tabs
              tabName="mainTab"
              defaultTab={mainTab}
              tabs={requestTabs}
              onChange={(key) => {
                setMainTab(key);
                setSearchParams((prev) => {
                  const params = new URLSearchParams(prev);
                  params.set('subTab', 'PENDING');
                  params.set('mainTab', key);
                  return params;
                });
              }}
            />
          </motion.div>

          {/* Unplanned Leaves Tab */}
          {mainTab === 'unplanned' && (
            <motion.div variants={itemVariants}>
              <UnplannedLeavesTab
                user={user}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                setSelectedMonth={setSelectedMonth}
                setSelectedYear={setSelectedYear}
              />
            </motion.div>
          )}

          {/* Leave Requests Tab */}
          {mainTab === 'leave' && (
            <motion.div className="space-y-4" variants={itemVariants}>
              {/* Status Filter Tabs */}
              <div className="flex items-center gap-4 justify-between">
                <Tabs
                  defaultTab={subTab}
                  variant="pills"
                  tabName="subTab"
                  tabs={leaveStatusTabs}
                  onChange={(key) => {
                    setSubTab(key);
                  }}
                />
              </div>

              {/* Leave Table */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {isLoadingEmployeeLeaves ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <TableSkeleton rows={4} columns={4} />
                  </table>
                ) : employeeLeaves.filter((leave) => leave.status === subTab)?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <Icon name="Calendar" size={48} className="mb-4 text-gray-400" />
                    <p className="text-lg font-medium">No leave requests found</p>
                    <p className="text-sm">Your leave requests will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto min-h-[600px]">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Leave Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Start Date/Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            End Date/Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duration
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reason
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {employeeLeaves
                          ?.filter((leave) => leave.status === subTab)
                          ?.map((leave) => {
                            const leaveType = mapLeaveType(leave?.type);
                            const isMultipleDay =
                              leave?.type === LEAVE_TYPES.MULTIPLE_DAY && leave?.dates?.length > 0;
                            const isExpanded = expandedRows.has(leave?.id);

                            return (
                              <React.Fragment key={leave?.id}>
                                <tr className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                      <Icon name="Calendar" size={16} className="text-gray-400" />
                                      <span className="text-sm font-medium text-gray-900">
                                        {dayjs(leave?.start_date_time).format('DD MMM YYYY')}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm text-gray-900">{leaveType}</span>
                                      {isMultipleDay && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                          {leave?.dates?.length} days
                                        </span>
                                      )}
                                      {isMultipleDay && (
                                        <button
                                          onClick={() => toggleRowExpansion(leave?.id)}
                                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                          title={isExpanded ? 'Hide dates' : 'Show all dates'}
                                        >
                                          <Icon
                                            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                                            size={16}
                                          />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                      <Icon name="Clock" size={14} className="text-gray-400" />
                                      <span className="text-sm text-gray-600">
                                        {displayStartTime(leave)}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                      <Icon name="Clock" size={14} className="text-gray-400" />
                                      <span className="text-sm text-gray-600">
                                        {displayEndTime(leave)}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      {leave?.duration}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div
                                      className="text-sm text-gray-600 max-w-xs truncate"
                                      title={leave?.reason}
                                    >
                                      {leave?.reason || 'No reason provided'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(leave?.status)}
                                  </td>
                                </tr>

                                {/* Expanded Row */}
                                {isMultipleDay && isExpanded && (
                                  <tr className="bg-blue-50">
                                    <td colSpan="8" className="px-6 py-4">
                                      <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                          <Icon
                                            name="Calendar"
                                            size={16}
                                            className="text-blue-600"
                                          />
                                          <h4 className="text-sm font-semibold text-gray-900">
                                            Leave Schedule ({leave?.dates?.length} days)
                                          </h4>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                          {[...(leave?.dates || [])].sort().map((date, index) => {
                                            const isCancelled =
                                              leave?.canceled_dates?.includes(date);
                                            const dateObj = new Date(date);
                                            const dayName = dateObj.toLocaleDateString('en-US', {
                                              weekday: 'short',
                                            });
                                            const formattedDate = dateObj.toLocaleDateString(
                                              'en-US',
                                              {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                              },
                                            );
                                            return (
                                              <div
                                                key={index}
                                                className={`flex items-center justify-between p-3 bg-white border border-${isCancelled ? 'red' : 'blue'}-200 rounded-lg hover:shadow-sm transition-shadow`}
                                              >
                                                <div className="flex items-center space-x-2">
                                                  <div
                                                    className={`w-8 h-8 bg-${isCancelled ? 'red' : 'blue'}-100 rounded-lg flex items-center justify-center`}
                                                  >
                                                    <span
                                                      className={`text-xs font-bold text-${isCancelled ? 'red' : 'blue'}-600`}
                                                    >
                                                      {dateObj.getDate()}
                                                    </span>
                                                  </div>
                                                  <div>
                                                    <p
                                                      className={`text-sm font-medium text-${isCancelled ? 'red' : 'blue'}-900`}
                                                    >
                                                      {dayName}, {formattedDate}
                                                    </p>
                                                    <p
                                                      className={`text-xs text-${isCancelled ? 'red' : 'blue'}-500`}
                                                    >
                                                      {workSchedule?.dayStartTime} -{' '}
                                                      {workSchedule?.dayEndTime}
                                                    </p>
                                                  </div>
                                                </div>
                                                {isCancelled && (
                                                  <span
                                                    title="Cancelled"
                                                    className="inline-flex items-center px-2 py-1.5 rounded-2xl text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 uppercase tracking-wider"
                                                  >
                                                    X
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {leave?.contact_number && (
                                          <div className="flex items-center space-x-2 pt-2">
                                            <Icon
                                              name="Phone"
                                              size={16}
                                              className="text-blue-600"
                                            />
                                            <span className="text-sm text-gray-700">
                                              Contact: {formatContact(leave.contact_number)}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Log Update Tab */}
          {mainTab === 'log-update' && (
            <motion.div className="space-y-4" variants={itemVariants}>
              <div className="flex items-center gap-4 justify-between">
                <div>
                  <div className="flex items-center gap-4">
                    <Tabs
                      defaultTab={subTab}
                      variant="pills"
                      tabName="subTab"
                      tabs={logUpdateStatusTabs}
                      onChange={(key) => {
                        setSubTab(key);
                      }}
                    />
                  </div>
                </div>
                <MonthYearNavigation
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  setSelectedMonth={setSelectedMonth}
                  setSelectedYear={setSelectedYear}
                />
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {isLoadingLogUpdateRequests ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <TableSkeleton rows={4} columns={4} />
                  </table>
                ) : logUpdateRequests?.data?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <Icon name="Clock" size={48} className="mb-4 text-gray-400" />
                    <p className="text-lg font-medium">No log update requests found</p>
                    <p className="text-sm">Your log update requests will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto min-h-[400px]">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Requested Times
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Original Times
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reason
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          {subTab === 'PENDING' && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {logUpdateRequests?.data?.map((request) => (
                          <tr key={request?.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {dayjs(request?.logDate).format('DD MMM YYYY')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="space-y-1">
                                <p className="text-xs text-blue-600 font-medium whitespace-nowrap">
                                  In: {dayjs(request.check_in).format('hh:mm A')}
                                </p>
                                <p className="text-xs text-blue-600 font-medium whitespace-nowrap">
                                  Out:{' '}
                                  {request.check_out
                                    ? dayjs(request.check_out).format('hh:mm A')
                                    : '--:--'}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="space-y-1 opacity-60">
                                <p className="text-xs text-gray-500 whitespace-nowrap">
                                  In:{' '}
                                  {request.originalCheckIn
                                    ? dayjs(request.originalCheckIn).format('hh:mm A')
                                    : '--:--'}
                                </p>
                                <p className="text-xs text-gray-500 whitespace-nowrap">
                                  Out:{' '}
                                  {request.originalCheckOut
                                    ? dayjs(request.originalCheckOut).format('hh:mm A')
                                    : '--:--'}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-600 max-w-xs">
                                {request.reason || '--'}
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(request.status)}
                            </td>
                            {subTab === 'PENDING' && (
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleCancelLogUpdate(request.id)}
                                  className="text-red-600 hover:text-red-900 text-sm font-medium transition-colors"
                                >
                                  Cancel
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>

      <ConfirmationModal
        isOpen={cancelModal.isOpen}
        isLoading={cancelModal.isLoading}
        onClose={() =>
          setCancelModal({
            isOpen: false,
            requestId: null,
            requestIds: null,
            isLoading: false,
            title: '',
            message: '',
          })
        }
        onConfirm={confirmCancelLogUpdate}
        title={cancelModal.title}
        message={cancelModal.message}
      />
    </div>
  );
};

export default EmployeeRequests;
